import { create } from 'zustand';

import { actAsUserIdAtom } from '@/services/atoms/auth';
import { store } from '@/services/store';

type ActingAsBannerState = {
  hiddenForUserId: string | undefined;
  hide: (userId: string) => void;
  reset: () => void;
};

/**
 * Which impersonated account's banner the reader has hidden this session.
 *
 * A hide is a choice about one impersonation, and the banner is the only
 * on-screen signal that requests are going out as someone else. So any change
 * to the acting-as user clears it, whichever path caused the change: switching
 * accounts, stopping, logging out, or restoring a saved session. Clearing on a
 * single subscription rather than at each call site is what keeps A, then B,
 * then A from finding the banner still hidden.
 *
 * Still keyed by account, so the banner cannot show for one account while
 * hidden state from another is in effect.
 *
 * In-memory on purpose. A cold start shows the banner again.
 */
export const useActingAsBannerStore = create<ActingAsBannerState>((set) => ({
  hiddenForUserId: undefined,
  hide: (userId) => set({ hiddenForUserId: userId }),
  reset: () => set({ hiddenForUserId: undefined }),
}));

store.sub(actAsUserIdAtom, () => {
  useActingAsBannerStore.getState().reset();
});
